import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../services/api';

const POLICY = [
  { id: 'len',   label: '8–12 characters',              test: (p) => p.length >= 8 && p.length <= 12 },
  { id: 'upper', label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'At least one number',           test: (p) => /\d/.test(p) },
  { id: 'spec',  label: 'At least one special character',test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(p) },
];

function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CheckIcon({ ok }) {
  return ok ? (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ) : (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

export default function ForceChangePassword() {
  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  const [oldPw, setOldPw]         = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showOld, setShowOld]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [showCon, setShowCon]     = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [success, setSuccess]     = useState(false);

  const checks = POLICY.map((p) => ({ ...p, ok: p.test(newPw) }));
  const allPass = checks.every((c) => c.ok);
  const matchOk = confirmPw.length > 0 && newPw === confirmPw;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!oldPw) { setError('Please enter your current password.'); return; }
    if (!allPass) { setError('New password does not meet the policy requirements.'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match.'); return; }
    setError('');
    setLoading(true);
    try {
      await auth.changePassword(oldPw, newPw);
      await logout();
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const s = {
    overlay: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    card: {
      background: 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '20px',
      padding: '40px',
      width: '100%',
      maxWidth: '440px',
      boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
    },
    lockIcon: {
      width: 52, height: 52,
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      borderRadius: '14px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px',
    },
    title: { color: '#f8fafc', fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 6 },
    subtitle: { color: '#94a3b8', fontSize: 13, textAlign: 'center', marginBottom: 28, lineHeight: 1.5 },
    label: { display: 'block', color: '#cbd5e1', fontSize: 13, fontWeight: 500, marginBottom: 6 },
    inputWrap: { position: 'relative', marginBottom: 16 },
    input: {
      width: '100%', boxSizing: 'border-box',
      background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 10, padding: '10px 40px 10px 14px',
      color: '#f8fafc', fontSize: 14, outline: 'none',
    },
    eyeBtn: {
      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
      background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
      display: 'flex', alignItems: 'center', padding: 2,
    },
    policyBox: {
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10, padding: '12px 14px', marginBottom: 16, marginTop: -8,
    },
    policyTitle: { color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 },
    policyItem: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, fontSize: 12 },
    error: {
      background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '10px 14px', color: '#fca5a5', fontSize: 13, marginBottom: 16,
    },
    btn: {
      width: '100%', padding: '12px', borderRadius: 10, border: 'none', cursor: 'pointer',
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      color: '#0f172a', fontWeight: 700, fontSize: 15, marginTop: 4,
    },
    logoutLink: {
      textAlign: 'center', marginTop: 16, color: '#64748b', fontSize: 12, cursor: 'pointer',
    },
    successCard: {
      background: 'rgba(34,197,94,0.08)',
      border: '1px solid rgba(34,197,94,0.3)',
      borderRadius: 16,
      padding: '36px 32px',
      textAlign: 'center',
    },
    successIcon: {
      width: 64, height: 64,
      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
      borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px',
    },
    loginBtn: {
      display: 'inline-block', marginTop: 24,
      padding: '12px 32px',
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      color: '#0f172a', fontWeight: 700, fontSize: 15,
      borderRadius: 10, textDecoration: 'none',
    },
  };

  return (
    <div style={s.overlay}>
      <div style={s.card}>
        {success ? (
          <div style={s.successCard}>
            <div style={s.successIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div style={{ color: '#86efac', fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
              Password Changed!
            </div>
            <div style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6 }}>
              Your password has been updated successfully.<br />
              Please sign in with your new password to continue.
            </div>
            <Link to="/login" style={s.loginBtn}>
              Go to Login Page →
            </Link>
          </div>
        ) : (
          <>
            <div style={s.lockIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div style={s.title}>Change Your Password</div>
            <div style={s.subtitle}>
              For security, you must set a new password before continuing.<br />
              This step cannot be skipped.
            </div>

            {error && <div style={s.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <label style={s.label}>Current Password</label>
              <div style={s.inputWrap}>
                <input
                  type={showOld ? 'text' : 'password'}
                  style={s.input}
                  value={oldPw}
                  onChange={(e) => setOldPw(e.target.value)}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                />
                <button type="button" style={s.eyeBtn} onClick={() => setShowOld(v => !v)}>
                  <EyeIcon open={showOld} />
                </button>
              </div>

              <label style={s.label}>New Password</label>
              <div style={s.inputWrap}>
                <input
                  type={showNew ? 'text' : 'password'}
                  style={s.input}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
                <button type="button" style={s.eyeBtn} onClick={() => setShowNew(v => !v)}>
                  <EyeIcon open={showNew} />
                </button>
              </div>

              {/* Password policy — always visible below new password */}
              <div style={s.policyBox}>
                <div style={s.policyTitle}>Password Requirements</div>
                {checks.map((c) => (
                  <div key={c.id} style={s.policyItem}>
                    <CheckIcon ok={c.ok} />
                    <span style={{ color: c.ok ? '#86efac' : '#94a3b8' }}>{c.label}</span>
                  </div>
                ))}
              </div>

              <label style={s.label}>Confirm New Password</label>
              <div style={s.inputWrap}>
                <input
                  type={showCon ? 'text' : 'password'}
                  style={{
                    ...s.input,
                    borderColor: confirmPw.length > 0
                      ? (matchOk ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)')
                      : 'rgba(255,255,255,0.12)',
                  }}
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                />
                <button type="button" style={s.eyeBtn} onClick={() => setShowCon(v => !v)}>
                  <EyeIcon open={showCon} />
                </button>
              </div>
              {confirmPw.length > 0 && !matchOk && (
                <div style={{ color: '#fca5a5', fontSize: 12, marginTop: -10, marginBottom: 14 }}>
                  Passwords do not match
                </div>
              )}

              <button type="submit" style={{ ...s.btn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
                {loading ? 'Updating…' : 'Set New Password'}
              </button>
            </form>

            <div style={s.logoutLink} onClick={logout}>
              Sign out instead
            </div>
          </>
        )}
      </div>
    </div>
  );
}
